// InicioView — KPIs + próximas provas da company

import { signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import {
  AlertCircle,
  Building2,
  Calendar,
  GraduationCap,
  Library,
  Loader2,
  Shield,
  Users,
  UsersRound
} from "lucide-react";
import { api, ApiError, type CompanyHome } from "../api";
import { friendlyError } from "@app/ui/lib/friendly-error";

const data = signal<CompanyHome | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const err = signal<string | null>(null);

async function load() {
  phase.value = "loading";
  err.value = null;
  try {
    data.value = await api.home();
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    err.value = friendlyError(e);
    phase.value = "error";
  }
}

export function InicioView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading")
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  if (phase.value === "error")
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" /> {err.value}
      </div>
    );
  const d = data.value!;
  return (
    <div class="space-y-6">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Building2 className="size-6 text-primary" />
          Início
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">Visão geral da sua organização.</p>
      </header>
      <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Professores" value={d.kpis.teachers} icon={GraduationCap} accent="violet" />
        <Kpi label="Alunos" value={d.kpis.students} icon={Users} accent="sky" />
        <Kpi label="Admins" value={d.kpis.admins} icon={Shield} accent="rose" />
        <Kpi label="Turmas" value={d.kpis.turmas} icon={UsersRound} accent="emerald" />
      </div>
      <section class="rounded-md border border-border bg-card/40 p-4">
        <h2 class="mb-2 flex items-center gap-2 text-sm font-bold">
          <Library className="size-4 text-primary" /> Disciplinas
        </h2>
        <div class="grid gap-2 sm:grid-cols-3">
          <Mini label="Em rascunho" value={d.kpis.disciplinas.draft} />
          <Mini label="Ativas" value={d.kpis.disciplinas.active} accent="emerald" />
          <Mini label="Encerradas" value={d.kpis.disciplinas.closed} accent="muted" />
        </div>
      </section>
      <section class="space-y-2 rounded-md border border-border bg-card/40 p-4">
        <h2 class="flex items-center gap-2 text-sm font-bold">
          <Calendar className="size-4 text-primary" /> Próximas provas
        </h2>
        {d.upcomingExams.length === 0 ? (
          <p class="text-xs italic text-muted-foreground">Nada agendado.</p>
        ) : (
          <ul class="space-y-1">
            {d.upcomingExams.map((e) => (
              <li
                key={e.id}
                class="flex items-center gap-3 rounded-md border border-border bg-background/50 px-3 py-2 text-xs"
              >
                <div class="min-w-0 flex-1">
                  <div class="truncate font-semibold">{e.title}</div>
                  <div class="font-mono text-[10px] text-muted-foreground">
                    {e.disciplinaName} · {e.startsAt && new Date(e.startsAt).toLocaleString("pt-BR")}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Kpi({
  label,
  value,
  icon: Icon,
  accent
}: {
  label: string;
  value: number;
  icon: typeof Users;
  accent: "violet" | "emerald" | "sky" | "rose" | "muted";
}) {
  const cls =
    accent === "violet"
      ? "bg-violet-500/10 text-violet-700 dark:text-violet-300"
      : accent === "emerald"
        ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
        : accent === "sky"
          ? "bg-sky-500/10 text-sky-700 dark:text-sky-300"
          : accent === "rose"
            ? "bg-rose-500/10 text-rose-700 dark:text-rose-300"
            : "bg-muted text-muted-foreground";
  return (
    <div class={`flex items-center gap-3 rounded-md border border-border px-4 py-3 ${cls}`}>
      <Icon className="size-5 shrink-0" />
      <div class="min-w-0">
        <div class="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
        <div class="text-2xl font-bold tabular-nums text-foreground">{value}</div>
      </div>
    </div>
  );
}

function Mini({
  label,
  value,
  accent
}: {
  label: string;
  value: number;
  accent?: "emerald" | "muted";
}) {
  const cls =
    accent === "emerald"
      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : accent === "muted"
        ? "bg-muted text-muted-foreground"
        : "bg-card";
  return (
    <div class={`rounded-md border border-border px-3 py-2 ${cls}`}>
      <div class="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div class="text-lg font-bold tabular-nums text-foreground">{value}</div>
    </div>
  );
}
